import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';

import { translate } from '@jsverse/transloco';
import { titleCase } from 'humanize-plus';

import { AppSharedModule } from 'app/app-shared.module';
import { LexXiFilterPillComponent } from 'app/components/xi-components/lex-xi-filter-pill/lex-xi-filter-pill.component';
import { Filter, OperatorType } from 'app/modules/collection/services/filter.service';
import { PredictiveDateRangeString } from 'app/modules/widget/components/predictive-widget-datepickers/predictive-widget-datepickers.component';
import { LexDatePipe } from 'app/pipes/lex-date.pipe';
import { WidgetGroup } from 'app/providers/bridge-api/dashboard.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import SidePaneItem from '../dashboard-side-pane/SidePaneItem';

import { DEFAULT_COMPARISON_COLOR } from '../../services/comparison-handler-service';
import { DatasourceManagerStore } from '../../stores/datasource-manager.store';
import { isDashboardLevelWidgetGroup } from '../../stores/widget-group.store';

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-group-side-pane-item',
  imports: [AppSharedModule, LexXiFilterPillComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './widget-group-side-pane-item.component.html',
  styleUrls: ['./widget-group-side-pane-item.component.scss'],
})
export class WidgetGroupSidePaneItemComponent
  extends SidePaneItem<WidgetGroup>
  implements OnChanges
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  private readonly datasourceManagerStore = inject(DatasourceManagerStore);
  // eslint-disable-next-line
  @Input('item') widgetGroup: WidgetGroup;

  itemType = 'widgetgroup';
  filterTooltip;
  actionTooltip;

  periodDateStrings: PredictiveDateRangeString = {
    previous: { first: null, last: null },
    current: { first: null, last: null },
  };

  scoreMetricShortLabel = '';
  scoreMetricLongLabel = '…';

  isDashboardLevel;

  constructor(
    private lexDatePipe: LexDatePipe,
    private accountService: StorageAccountService
  ) {
    super();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.isDisabled || changes.hasDataSources) {
      this.filterTooltip = undefined;
      this.actionTooltip = undefined;

      if (!changes.hasDataSources?.currentValue) {
        this.filterTooltip = translate('widget-group-side-pane-item-component.no-datasources');
      }

      if (changes.isDisabled?.currentValue) {
        this.filterTooltip = this.isDashboardLocked
          ? translate('widget-group-side-pane-item-component.dashboard-locked')
          : translate('widget-group-side-pane-item-component.bulk-apply-mode');
        this.actionTooltip = this.isDashboardLocked
          ? translate('widget-group-side-pane-item-component.dashboard-locked')
          : translate('widget-group-side-pane-item-component.bulk-apply-mode');
      }
    }

    if (changes.widgetGroup?.currentValue) {
      await this.accountService.groupFieldsSub.latest();

      this.scoreMetricShortLabel = translate('widget-group-side-pane-item-component.score-metric', {
        metric: FieldService.getFieldShortName(this.widgetGroup.parameters?.scoreMetric),
      });
      this.scoreMetricLongLabel = translate('widget-group-side-pane-item-component.score-metric', {
        metric: FieldService.getFieldLongName(this.widgetGroup.parameters?.scoreMetric),
      });

      if (this.widgetGroup.isImpact) {
        this.itemAction.emit({ action: 'expand', data: true });
      }

      this.isDashboardLevel = isDashboardLevelWidgetGroup(this.widgetGroup);
    }

    const dataSource = this.datasourceManagerStore.getById(this.widgetGroup?.lastUsedDataSource);
    if (dataSource) {
      this.widgetGroup.lastUsedDataSourceDisplayName = dataSource.displayName;
    }
  }

  getWidgetCount(widgetGroup: WidgetGroup): number {
    if (!widgetGroup.parentWidget || widgetGroup.widgets.includes(widgetGroup.parentWidget)) {
      return widgetGroup.widgets.length;
    }

    return widgetGroup.widgets.length + 1;
  }

  getFilterCount(widgetGroup: WidgetGroup): number {
    const count = widgetGroup.parameters.scoreMetric ? 3 : 0;

    return (widgetGroup.andFilters?.length || 0) + count;
  }

  onItemRenameClicked($event) {
    if (this.isDisabled) {
      return;
    }

    $event.stopPropagation();

    this.triggerItemAction('rename');
  }

  triggerItemAction(action, data?) {
    this.itemAction.emit({ action, data });
  }

  onFilterAction(
    $event: { filterMoveOrDelete: OperatorType | 'remove'; newFilter: Filter },
    operator: OperatorType,
    filter: Filter
  ) {
    if (this.isDisabled) {
      return;
    }
    if ($event.filterMoveOrDelete) {
      const action = $event.filterMoveOrDelete;
      if (action === operator) {
        return;
      }
      this.filterAction(action, operator, filter);
      this.itemAction.emit({ action: action === 'remove' ? 'filterDelete' : 'filterMove' });
    } else {
      this.itemAction.emit({ action: 'filterChange' });
    }
  }

  private filterAction(
    action: 'remove' | OperatorType,
    prevOperator: OperatorType,
    originalFilter: Filter
  ) {
    let filterToUpdate: Filter;

    if (originalFilter.parentFilter) {
      for (const key in this.widgetGroup.parentFilters) {
        if (this.widgetGroup.parentFilters.hasOwnProperty(key)) {
          this.widgetGroup.parentFilters[key][prevOperator].forEach((item, index) => {
            if (
              item.analyticItem === originalFilter.analyticItem &&
              item.value === originalFilter.value
            ) {
              //remove old filter
              filterToUpdate = this.widgetGroup.parentFilters[key][prevOperator].splice(index, 1);
              //move filter
              if (action !== 'remove') {
                this.widgetGroup.parentFilters[key][action].push(filterToUpdate[0]);
              }
            }
          });
        }
      }
    } else {
      for (const key in this.widgetGroup.filters) {
        if (this.widgetGroup.filters.hasOwnProperty(key)) {
          this.widgetGroup.filters[key][prevOperator].forEach((item, index) => {
            if (
              item.analyticItem === originalFilter.analyticItem &&
              item.value === originalFilter.value
            ) {
              filterToUpdate = this.widgetGroup.filters[key][prevOperator].splice(index, 1);
              if (action !== 'remove') {
                this.widgetGroup.filters[key][action].push(filterToUpdate[0]);
              }
            }
          });
        }
      }
    }
  }

  getPeriodLabel(periodPrefix: 'previous' | 'current', filterGroup): string {
    const newStartDate = filterGroup.parameters?.[`${periodPrefix}Start`];
    const newEndDate = filterGroup.parameters?.[`${periodPrefix}End`];
    let label = this[`${periodPrefix}PeriodLabel`];
    const oldStartDate = this.periodDateStrings[periodPrefix].first;
    const oldEndDate = this.periodDateStrings[periodPrefix].last;

    if (!label || oldStartDate !== newStartDate || oldEndDate !== newEndDate) {
      const text = titleCase(periodPrefix);
      const start = this.lexDatePipe.transform(newStartDate);
      const end = this.lexDatePipe.transform(newEndDate);

      label = translate('widget-group-side-pane-item-component.x-y-z', {
        x: text,
        y: start,
        z: end,
      });
      this[`${periodPrefix}PeriodLabel`] = label;
      this.periodDateStrings[periodPrefix].first = newStartDate;
      this.periodDateStrings[periodPrefix].last = newEndDate;
    }

    return label;
  }

  get item() {
    return this.widgetGroup;
  }

  get widgetGroupColor() {
    return this.widgetGroup.color ?? DEFAULT_COMPARISON_COLOR;
  }
}
